/**
 * Announcements API Client — data behind "What's New".
 *
 * API Endpoints:
 * - GET /announcements?flags=&since= → unread (or since-cursor) announcements, 30-day cap
 * - POST /announcements/mark-read { last_shown_at? } → advances users.last_announcements_read_at
 *   (BE clamps: never backwards, never past now)
 */

import type { Announcement, AnnouncementsListParams, AnnouncementsPage } from '@/types';
import { apiClient } from './base-client';

interface RawAnnouncementsPayload {
  effective_since?: string;
  announcements?: Announcement[];
  meta?: { total?: number; last_page?: number };
}

interface RawMarkReadPayload {
  last_announcements_read_at?: string;
}

// BE `sendResponse` spreads the payload at the top level (`{ message, ...payload }`),
// although api-announcements.md shows a `{ success, data }` envelope. Accept both
// so a BE-side switch to the documented shape can't blank the feature again.
type Raw<T> = (T & { data?: T }) | null | undefined;

function payloadOf<T extends object>(res: Raw<T>): Partial<T> {
  if (!res || typeof res !== 'object') return {};
  return res.data && typeof res.data === 'object' ? res.data : res;
}

export const announcementsClient = {
  list: async ({ flags, since }: AnnouncementsListParams): Promise<AnnouncementsPage> => {
    const res = await apiClient.get<Raw<RawAnnouncementsPayload>>('/announcements', {
      params: { flags: flags.join(','), ...(since ? { since } : {}) },
    });
    const body = payloadOf(res);
    const announcements = Array.isArray(body.announcements) ? body.announcements : [];
    return {
      effective_since: body.effective_since ?? null,
      announcements,
      total: body.meta?.total ?? announcements.length,
      last_page: body.meta?.last_page ?? 1,
    };
  },

  /**
   * `lastShownAt` = newest `published_at` the user actually saw; omitted → BE
   * uses now(). Returns the server's new cursor, or null for a malformed body.
   */
  markRead: async (lastShownAt?: string): Promise<string | null> => {
    const res = await apiClient.post<Raw<RawMarkReadPayload>>(
      '/announcements/mark-read',
      lastShownAt ? { last_shown_at: lastShownAt } : {}
    );
    return payloadOf(res).last_announcements_read_at ?? null;
  },
};
